import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse, IconButton, Box } from '@mui/material';
import { FaEye, FaEyeSlash, FaChevronDown, FaChevronUp, FaUserCircle, FaBars, FaHome, FaCog, FaFileUpload, FaClipboardList } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CallLogUploader from './CallLogUploader.js';
import BatchLookup from './BatchLookup.js';
import AHTOps from './AHTOps.js';
import AHTOpsBatchLookup from './AHTOpsBatchLookup.js';
import './App.css';

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [isTrackersMenuOpen, setIsTrackersMenuOpen] = useState(false);
  const [isAhtMenuOpen, setIsAhtMenuOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('welcome');
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  useEffect(() => {
    // Verificar si ya hay una sesión activa
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      setCurrentPage('welcome');
      setIsTrackersMenuOpen(false);
      setIsAhtMenuOpen(false);
      setIsMenuOpen(false);
    }
  }, [session]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
      toast.error(`Error al iniciar sesión: ${error.message}`);
    } else {
      toast.success("¡Inicio de sesión exitoso!");
      // El useEffect manejará la actualización de la sesión
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    setIsProfileMenuOpen(false);
    setCurrentPage('welcome');
    setIsTrackersMenuOpen(false);
    setIsAhtMenuOpen(false);
    setIsMenuOpen(false);
    await supabase.auth.signOut();
  };

  const navigateAndCloseMenu = (page) => {
    setCurrentPage(page);
    setIsMenuOpen(false);
  };

  // Si el usuario ya está logueado, mostramos pantalla de bienvenida
  if (session) {
    return (
      <div id="outer-container">
        <ToastContainer position="bottom-right" autoClose={5000} />
        {/* Botón de Menú (Hamburguesa) */}
        <IconButton 
          onClick={() => setIsMenuOpen(true)} 
          sx={{ position: 'fixed', top: 20, left: 20, zIndex: 200, color: '#333' }}
        >
          <FaBars size={24} />
        </IconButton>

        {/* Drawer de Material UI */}
        <Drawer
          anchor="left"
          open={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 220,
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(6px)',
            },
          }}
        >
          <Box sx={{ width: 220 }} role="presentation">
            <List dense>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => navigateAndCloseMenu('welcome')}
                  sx={{ px: 1.5, py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}><FaHome size={16} /></ListItemIcon>
                  <ListItemText primary="Inicio" primaryTypographyProps={{ fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setIsTrackersMenuOpen(!isTrackersMenuOpen);
                    // Optional: close the other section for cleaner UX
                    setIsAhtMenuOpen(false);
                  }}
                  sx={{ px: 1.5, py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}><FaCog size={16} /></ListItemIcon>
                  <ListItemText primary="Trackers" primaryTypographyProps={{ fontSize: '0.9rem' }} />
                  {isTrackersMenuOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </ListItemButton>
              </ListItem>
              <Collapse in={isTrackersMenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    sx={{ pl: 3.5, py: 0.5 }}
                    onClick={() => navigateAndCloseMenu('uploadLogs')}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}><FaFileUpload size={16} /></ListItemIcon>
                    <ListItemText primary="Cargar Trackers" primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>
                  <ListItemButton
                    sx={{ pl: 3.5, py: 0.5 }}
                    onClick={() => navigateAndCloseMenu('batchLookup')}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}><FaClipboardList size={16} /></ListItemIcon>
                    <ListItemText primary="Consulta de Trackers" primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>
                </List>
              </Collapse>

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setIsAhtMenuOpen(!isAhtMenuOpen);
                    // Optional: close the other section for cleaner UX
                    setIsTrackersMenuOpen(false);
                  }}
                  sx={{ px: 1.5, py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}><FaCog size={16} /></ListItemIcon>
                  <ListItemText primary="AHT" primaryTypographyProps={{ fontSize: '0.9rem' }} />
                  {isAhtMenuOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </ListItemButton>
              </ListItem>
              <Collapse in={isAhtMenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    sx={{ pl: 3.5, py: 0.5 }}
                    onClick={() => navigateAndCloseMenu('uploadAhtOps')}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}><FaFileUpload size={16} /></ListItemIcon>
                    <ListItemText primary="Cargar AHT Ops" primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>
                  <ListItemButton
                    sx={{ pl: 3.5, py: 0.5 }}
                    onClick={() => navigateAndCloseMenu('batchLookupAhtOps')}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}><FaClipboardList size={16} /></ListItemIcon>
                    <ListItemText primary="Consulta AHT Ops" primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
          </Box>
        </Drawer>

        <main id="page-wrap" className="app-container" style={{alignItems: 'flex-start', paddingTop: '50px'}}>
          <div className="top-header">
            <div className="profile-section" onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}>
              <FaUserCircle className="profile-icon" />
              <span className="profile-email">{session.user.email}</span>
              {isProfileMenuOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              
              {isProfileMenuOpen && (
                <div className="profile-dropdown">
                  <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); alert('Editar Perfil'); }}>Editar Perfil</button>
                  <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleLogout(); }}>Cerrar Sesión</button>
                </div>
              )}
            </div>
          </div>

          {currentPage === 'welcome' && (
            <div className="welcome-container" style={{ flexDirection: 'column' }}>
              <img src="/images/MVW_Logo.jpg" alt="Logo Central" className="welcome-logo-large" />
              <div className="welcome-text-container">
                <h4 className="welcome-title">OperationsGT</h4>
              </div>
            </div>
          )}
          {currentPage === 'uploadLogs' && <CallLogUploader session={session} onCancel={() => setCurrentPage('welcome')} />}
          {currentPage === 'uploadAhtOps' && <AHTOps session={session} onCancel={() => setCurrentPage('welcome')} />}
          {currentPage === 'batchLookup' && <BatchLookup />}
          {currentPage === 'batchLookupAhtOps' && <AHTOpsBatchLookup />}
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <ToastContainer position="bottom-right" />
      <div className="login-card">
        <img 
          src="/images/MVW_Logo.jpg"
          alt="Logo de la Empresa"
          className="login-logo" 
        />
        <p className="logo-subtitle">OperationsGT</p>
        <h2>Iniciar Sesión</h2>
        <form onSubmit={handleLogin}>
          <div className="input-group">
            <label htmlFor="email">Correo Electrónico</label>
            <input
              id="email"
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="password">Contraseña</label>
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="eye-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {error && <p className="error-message">{error}</p>}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Cargando...' : 'Entrar'}
          </button>
        </form>
      </div>
      <div className="login-footer">
        v.{process.env.REACT_APP_GIT_VERSION || 'Dev'} Synapsis (ai)
      </div>
    </div>
  );
}

export default App;
